"use client";

/**
 * Fashion Shop Product Card
 *
 * Identical Buy-button pattern to ElectronicsShopProductCard:
 *  - `absolute bottom-2 right-2` inside the image container
 *  - ALWAYS visible (no hover gate — works on mobile and desktop)
 *  - Small rounded-full pill: ShoppingCart icon + "Buy" text
 *  - Turns green while adding to cart
 *  - Clicking opens QuickVariantSelector (POS-replica dialog) for variant products,
 *    or adds directly for products with no variants
 *  - After successful add, opens the cart drawer
 */

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Heart, ShoppingCart } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { useCartStore } from "@/lib/store/cartStore";
import { useHydrated } from "@/hooks/useHydrated";
import { QuickVariantSelector } from "./QuickVariantSelector";
import { ShopProduct } from "@/lib/types/shop";
import { Id } from "@/convex/_generated/dataModel";
import ProductInquiryDialog from "@/components/product/ProductInquiryDialog";
import { HirePriceTag } from "./HirePriceTag";
import { WebsitePerkTags } from "@/components/product/WebsitePerks";
import { ConditionBadge } from "@/components/product/ConditionBadge";
import { isForHire, isHireOnly } from "@/lib/config/hire";
import { optimizeBunnyUrl } from "@/lib/utils/bunnyOptimizer";
import { showSizesOnWebsite } from "@/lib/config/dimensions";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useSession } from "@/lib/auth/auth-client";

interface FashionShopProductCardProps {
  product: ShopProduct;
  className?: string;
  productUrlPrefix?: string;
}

export function FashionShopProductCard({
  product,
  className,
  productUrlPrefix = "",
}: FashionShopProductCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [toggling, setToggling] = useState(false);
  const hydrated = useHydrated();

  const { data: session } = useSession();
  const isLoggedIn = !!session?.user;

  // @ts-ignore TS2589 deep-instantiation false-positive
  const addToCartMutation = useMutation(api.cart.core.addToCart);
  // @ts-ignore TS2589
  const toggleFavoriteMutation = useMutation(api.customers.customerDashboard.toggleFavorite);

  // Live wishlist state — only query when logged in
  const isFavoritedResult = useQuery(
    api.customers.customerDashboard.isFavorited,
    isLoggedIn ? { productId: product._id as Id<"products"> } : "skip",
  );
  const isWishlisted = isFavoritedResult === true;

  const { openCart, sessionId } = useCartStore();

  // ── Stock ──────────────────────────────────────────────────────────────────
  const actualTotalStock =
    product.variants && product.variants.length > 0
      ? product.variants.reduce((t: number, v: any) => t + (v.stock || 0), 0)
      : product.totalStock || product.stock || 0;
  const isInStock = product.inStock ?? actualTotalStock > 0;
  // Pre-order: a flagged product is purchasable even at 0 stock — recorded as a
  // pre-order at checkout (stock isn't allocated until it arrives).
  const isPreorder = !!(product as any).preorder?.enabled;

  // ── Derived ────────────────────────────────────────────────────────────────
  const brandName = product.brandName || "Unknown Brand";
  const brandSlug = product.brandSlug;
  const productUrl = `${productUrlPrefix}/product/${product.slug || product._id}`;

  const mainImage =
    product.images && product.images.length > 0
      ? product.images[0]
      : "/images/placeholder-product.jpg";
  const hoverImage =
    product.images && product.images.length > 1 ? product.images[1] : null;

  const discountPct =
    product.comparePrice && product.comparePrice > product.price
      ? Math.round((1 - product.price / product.comparePrice) * 100)
      : 0;

  const hasSizes = (product.fashionAttributes?.sizes?.length ?? 0) > 0;
  const hasColors = (product.fashionAttributes?.colors?.length ?? 0) > 0;
  const hasDesigns = (product.fashionAttributes?.designs?.length ?? 0) > 0;
  const hasAnyVariants = hasSizes || hasColors || hasDesigns;

  // ── Add-to-cart core ───────────────────────────────────────────────────────
  const addToCart = async (
    qty: number,
    variantId?: string,
    productData?: any,
  ) => {
    const brandId = (product as any).brandId as Id<"brands"> | undefined;
    if (!brandId) {
      toast.error("Cannot add to cart — brand info missing");
      return;
    }
    const selectedVariants = productData?.selectedVariants;
    await addToCartMutation({
      productId: product._id as Id<"products">,
      brandId,
      quantity: qty,
      variantId,
      selectedVariants: selectedVariants
        ? {
            size: selectedVariants.size || undefined,
            color: selectedVariants.color || undefined,
            colorHex: selectedVariants.colorHex || undefined,
            isMulticolor: selectedVariants.isMulticolor || undefined,
            multicolorList: selectedVariants.multicolorList || undefined,
          }
        : undefined,
      sessionId: sessionId || undefined,
    });
  };

  // ── Buy-button click ───────────────────────────────────────────────────────
  const handleBuyClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (product.isCustomPrice) { setIsInquiryOpen(true); return; }
    if (!isInStock && !isPreorder) { toast.error("Out of stock"); return; }

    // If product has variants → open selector dialog
    if (hasAnyVariants) { setShowVariantModal(true); return; }

    // No variants → add directly
    setAdding(true);
    try {
      await addToCart(1);
      toast.success("Added to cart!");
      openCart();
    } catch (err: any) {
      toast.error(err?.message || "Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };

  // Called by QuickVariantSelector when customer confirms
  const handleVariantConfirm = async (
    qty: number,
    variantId?: string,
    productData?: any,
  ) => {
    setAdding(true);
    try {
      await addToCart(qty, variantId, productData);
      toast.success("Added to cart!");
      openCart();
    } catch (err: any) {
      toast.error(err?.message || "Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };


  // ── Wishlist toggle ────────────────────────────────────────────────────────
  const handleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      toast.error("Sign in to save to your wishlist", {
        action: { label: "Sign in", onClick: () => { window.location.href = "/signin"; } },
      });
      return;
    }
    if (toggling) return;
    const brandId = (product as any).brandId as Id<"brands"> | undefined;
    if (!brandId) return;
    setToggling(true);
    try {
      const result = await toggleFavoriteMutation({
        productId: product._id as Id<"products">,
        brandId,
      });
      toast.success(result.isFavorited ? "Added to wishlist ❤️" : "Removed from wishlist");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update wishlist");
    } finally {
      setToggling(false);
    }
  };

  const isListView = className?.includes("flex flex-row");

  return (
    <div className={cn("group relative p-0 bg-white", className)}>

      {/* ── Image container ──────────────────────────────────────────────── */}
      <div
        className={cn(
          "relative overflow-hidden cursor-pointer bg-white mb-1",
          isListView ? "w-48 h-64 flex-shrink-0" : "aspect-[3/4]",
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Link href={productUrl}>
          {/* Main image */}
          <Image
            src={optimizeBunnyUrl(mainImage, { width: 600 })}
            alt={product.name}
            fill
            unoptimized
            className={cn(
              "object-cover object-center transition-all duration-700 ease-in-out",
              hoverImage && isHovered
                ? "opacity-0 scale-110"
                : "opacity-100 scale-100 group-hover:scale-105",
            )}
            sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
          />
          {/* Hover image swap */}
          {hoverImage && (
            <Image
              src={optimizeBunnyUrl(hoverImage, { width: 600 })}
              alt={`${product.name} - alternate`}
              fill
              unoptimized
              className={cn(
                "object-cover object-center transition-all duration-700 ease-in-out",
                isHovered ? "opacity-100 scale-105" : "opacity-0 scale-95",
              )}
              sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
            />
          )}
        </Link>

        {/* Discount badge */}
        {discountPct > 0 && (
          <div className="absolute top-2 left-2 z-10 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
            -{discountPct}%
          </div>
        )}

        {/* New or used, in the shop's own words — nothing for plain New
            (lib/config/productCondition.ts; Bagz Phones, Oct 2026). */}
        <ConditionBadge product={product} className="absolute bottom-2 left-2 z-10 max-w-[80%]" />

        {/* Wishlist heart — top-right, always visible, turns red when saved */}
        <button
          onClick={handleWishlist}
          disabled={toggling}
          aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
          className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-all shadow-sm"
        >
          <Heart
            className={cn(
              "h-3.5 w-3.5 transition-all duration-200",
              isWishlisted
                ? "fill-red-500 text-red-500 scale-110"
                : "text-gray-500 hover:text-red-400",
              toggling && "opacity-50",
            )}
          />
        </button>

        {/* Out-of-stock / pre-order overlay */}
        {!isInStock && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className={cn(
              "text-xs font-semibold px-2 py-1 rounded-full",
              isPreorder ? "bg-amber-400 text-amber-950" : "bg-black/60 text-white"
            )}>
              {isPreorder ? "Pre-order" : "Out of Stock"}
            </span>
          </div>
        )}
      </div>

      {/* ── Product info — 3 clean rows ─────────────────────────────────── */}
      <div className="space-y-0.5">

        {/* Row 1: Product name — plain text, no underline */}
        <Link href={productUrl} style={{ textDecoration: 'none' }}>
          <h3 className="text-xs sm:text-sm text-gray-900 font-medium leading-tight line-clamp-1">
            {product.name}
          </h3>
        </Link>

        {/* Row 2: Sizes + colour swatches. Sizes only when the shop shows
            them on its website (an events shop hides them unless it hires). */}
        <div className="flex items-center gap-2 flex-wrap">
          {showSizesOnWebsite(product as any) && product.fashionAttributes?.sizes && product.fashionAttributes.sizes.length > 0 && (
            <div className="flex flex-nowrap gap-1 overflow-x-auto scrollbar-hide">
              {product.fashionAttributes.sizes.slice(0, 5).map((size, i) => (
                <span
                  key={i}
                  className="text-[8px] sm:text-[9px] px-1 py-0.5 border border-gray-200 rounded text-gray-500 font-medium whitespace-nowrap flex-shrink-0"
                >
                  {size}
                </span>
              ))}
              {product.fashionAttributes.sizes.length > 5 && (
                <span className="text-[8px] sm:text-[9px] text-gray-400 whitespace-nowrap flex-shrink-0">
                  +{product.fashionAttributes.sizes.length - 5}
                </span>
              )}
            </div>
          )}
          {product.colorVariants && product.colorVariants.length > 0 && (
            <div className="flex items-center gap-0.5 flex-nowrap">
              {product.colorVariants.slice(0, 6).map((color, i) => (
                <div
                  key={i}
                  className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full border border-gray-200 flex-shrink-0"
                  style={{ backgroundColor: color.hex || "#ccc" }}
                  title={color.name}
                />
              ))}
              {product.colorVariants.length > 6 && (
                <span className="text-[8px] sm:text-[9px] text-gray-400 ml-0.5 whitespace-nowrap flex-shrink-0">
                  +{product.colorVariants.length - 6}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Row 3: Price (left) + Buy / Enquire button (right) */}
        <div className="flex items-center justify-between gap-2">
          {/* Price */}
          <div className="flex-1 min-w-0">
            {isHireOnly(product) ? (
              <HirePriceTag product={product} solo />
            ) : product.isCustomPrice ? (
              <span className="text-[10px] sm:text-xs font-medium text-amber-700">Enquire for Price</span>
            ) : product.priceHidden ? (
              <span className="text-[10px] sm:text-xs font-medium text-gray-400 italic">Sign in for Price</span>
            ) : discountPct > 0 ? (
              <div className="flex items-baseline gap-1">
                <span className="text-xs sm:text-sm font-bold text-black">
                  {product.currency || "UGX"} {product.price.toLocaleString()}
                </span>
                <span className="text-[9px] line-through text-gray-400">
                  {(product.comparePrice || 0).toLocaleString()}
                </span>
              </div>
            ) : (
              <span className="text-xs sm:text-sm font-bold text-black">
                {product.currency || "UGX"} {(product.price || 0).toLocaleString()}
              </span>
            )}
            {isForHire(product) && !isHireOnly(product) && (
              <div className="mt-0.5">
                <HirePriceTag product={product} />
              </div>
            )}
            {/* Free delivery, and its add-ons (website, Oct 2026). */}
            <WebsitePerkTags product={product} className="mt-0.5" />
          </div>

          {/* Buy / Enquire button — same visual weight as the price text.
              Hire-only: no cart — "Hire" leads to the page and its request form. */}
          {isHireOnly(product) ? (
            <Link
              href={productUrl}
              className="flex-shrink-0 rounded-full border border-amber-300 bg-amber-50 text-amber-700 px-2 py-0.5 text-[10px] font-semibold hover:bg-amber-100 transition-colors"
            >
              Hire
            </Link>
          ) : product.isCustomPrice ? (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsInquiryOpen(true); }}
              className="flex-shrink-0 rounded-full border border-amber-300 bg-amber-50 text-amber-700 px-2 py-0.5 text-[10px] font-semibold hover:bg-amber-100 transition-colors"
            >
              Enquire
            </button>
          ) : isInStock ? (
            <button
              onClick={handleBuyClick}
              disabled={adding}
              aria-label="Buy"
              className={cn(
                "flex-shrink-0 flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-all duration-200",
                adding
                  ? "bg-green-500 border-green-500 text-white"
                  : "bg-white border-gray-300 text-gray-800 hover:bg-black hover:text-white hover:border-black"
              )}
            >
              <ShoppingCart className="w-3 h-3" strokeWidth={2.5} />
              {adding ? "" : "Buy"}
            </button>
          ) : isPreorder ? (
            <button
              onClick={handleBuyClick}
              disabled={adding}
              aria-label="Pre-order"
              className={cn(
                "flex-shrink-0 flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-all duration-200",
                adding
                  ? "bg-amber-500 border-amber-500 text-white"
                  : "bg-amber-400 border-amber-400 text-amber-950 hover:bg-amber-500 hover:text-white hover:border-amber-500"
              )}
            >
              <ShoppingCart className="w-3 h-3" strokeWidth={2.5} />
              {adding ? "" : "Pre-order"}
            </button>
          ) : (
            <span className="flex-shrink-0 text-[10px] text-red-400 font-medium">Sold out</span>
          )}
        </div>
      </div>

      {/* ── QuickVariantSelector (POS-replica dialog) ───────────────────── */}
      <QuickVariantSelector
        isOpen={showVariantModal}
        onClose={() => setShowVariantModal(false)}
        product={{
          _id: product._id,
          name: product.name,
          slug: product.slug,
          basePrice: product.price,
          comparePrice: product.comparePrice,
          images: product.images,
          totalStock: actualTotalStock,
          brandName,
          brandSlug,
          subcategoryName: product.subcategoryName,
          subcategorySlug: product.subcategorySlug,
          productTypeName: product.productTypeName,
          productTypeSlug: product.productTypeSlug,
          fashionAttributes: product.fashionAttributes
            ? {
                sizes: product.fashionAttributes.sizes,
                colors: product.fashionAttributes.colors?.map((c) => ({
                  id: (c as any).id,
                  name: c.name,
                  hex: c.hex,
                  isMulticolor: (c as any).isMulticolor,
                  multicolorList: (c as any).multicolorList,
                  images: (c as any).images,
                })),
                designs: product.fashionAttributes.designs,
              }
            : undefined,
        }}
        onAddToCart={handleVariantConfirm}
      />

      {/* Product Inquiry Dialog */}
      {product.isCustomPrice && (
        <ProductInquiryDialog
          isOpen={isInquiryOpen}
          onClose={() => setIsInquiryOpen(false)}
          productName={product.name}
          productImage={product.images?.[0]}
          brandName={brandName}
          brandId={product.brandId || ""}
          productId={product._id}
        />
      )}
    </div>
  );
}
